/**
 * جدولِ تفکیکِ آدرس در سربرگِ «آدرس‌ها» (WHOSUP-277).
 *
 * هر ردیف یک ساختمان: شهرِ منبع، شهرِ رویداد، کشور، استان، محله، خیابان،
 * پلاک، همه‌ی سالن‌ها، کدپستی، مختصات، فلگ، شمارِ رویدادهای زنده و گذشته و
 * یک رویدادِ نمونه. داده را کرالر می‌سازد (WHOSUP-274/276) و مایگریشنِ ۰۱۵۷
 * با شمارِ رویداد و فلگ‌های زنده پس می‌دهد.
 *
 * • روی هر خانه بایست تا ببینی از کجا آمده: منبع / قاعده / مدل، و مقدارِ
 *   رقیبی که منبعِ دیگری داده بود.
 * • قرمز یعنی «نگاه کن» — قاعده‌ها در `core/lib/addressTable.ts`.
 * • ستونِ «فلگ» چندانتخابی است و در `crawler.address_flags` می‌نشیند؛ کرالر
 *   شبانه برش می‌دارد.
 *
 * این فایل فقط رندر می‌کند؛ قرمز، مرتب‌سازی و فیلتر همه در core‌اند.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import { describeError, reportError } from '@/core/lib/report';
import {
  COLUMNS,
  COLUMN_LABEL,
  FLAGGABLE,
  buildAddressTable,
  cellProvenance,
  filterRows,
  sortRows,
  type AddressRow,
  type ColKey,
  type FlagCol,
  type SortState,
  type TableFilter,
} from '@/core/lib/addressTable';
import { Hint } from '@/ui/Hint';
import { fmtNum as fmt } from '@/ui/Num';

// از این طول به بعد متنِ کامل در title هم می‌آید. بریدنِ خودِ متن کارِ CSS است
// (`.addrtbl .cut`): مرورگر max-width را روی td نادیده می‌گیرد، پس سقف روی
// span است — وگرنه یک شهرِ منبعِ چندتایی کلِ ستون را پهن می‌کرد.
const CUT = 20;

function Text({ value }: { value: string }) {
  if (!value) return <span className="dim">—</span>;
  return <span className="cut">{value}</span>;
}

/** چندانتخابیِ فلگ: فهرستِ ستون‌ها، با ذخیره‌ی صریح. */
function FlagPicker({ row, busy, disabled, onSave }: {
  row: AddressRow; busy: boolean; disabled: boolean;
  onSave: (cols: FlagCol[]) => void;
}) {
  const [draft, setDraft] = useState<FlagCol[]>(row.flags);
  const dirty = draft.length !== row.flags.length || draft.some((c) => !row.flags.includes(c));
  return (
    <details className="addrflags"
      onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) setDraft(row.flags); }}>
      <summary>
        {row.flags.length > 0
          ? row.flags.map((f) => COLUMN_LABEL[f]).join('، ')
          : <span className="dim">＋</span>}
      </summary>
      <div className="addrflags-pop">
        {FLAGGABLE.map((c) => (
          <label key={c} className="markcell">
            <input type="checkbox" checked={draft.includes(c)} disabled={disabled || busy}
              onChange={(e) => setDraft(e.target.checked
                ? [...draft, c] : draft.filter((x) => x !== c))} />
            {COLUMN_LABEL[c]}
          </label>
        ))}
        <button type="button" className="btn" disabled={disabled || busy || !dirty}
          onClick={() => onSave(draft)}>
          ذخیره
        </button>
        {row.flaggedAt ? <span className="dim">آخرین تغییر: {formatDateTime(row.flaggedAt)}</span> : null}
      </div>
    </details>
  );
}

function Cell({ row, col }: { row: AddressRow; col: ColKey }) {
  const v = row.cells[col];
  if (col === 'sample') {
    if (!row.sample) return <span className="dim">—</span>;
    const t = row.sample.title || row.sample.id;
    return row.sample.url
      ? <a href={row.sample.url} target="_blank" rel="noreferrer"><Text value={t} /></a>
      : <Text value={t} />;
  }
  if (col === 'live_events' || col === 'past_events') return <>{fmt(Number(v))}</>;
  const mono = col === 'coordinates' || col === 'postal_code' || col === 'number';
  return mono ? <span className="mono ltr"><Text value={v} /></span> : <Text value={v} />;
}

const EMPTY_FILTER: TableFilter = { cols: {}, onlySuspect: false };

export function AddressTable() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['addressTable'], queryFn: () => opsRepo.addressTable() });
  const [sort, setSort] = useState<SortState>({ col: 'live_events', dir: 'desc' });
  const [filter, setFilter] = useState<TableFilter>(EMPTY_FILTER);
  const [err, setErr] = useState<string | null>(null);

  const t = useMemo(() => (q.data ? buildAddressTable(q.data) : null), [q.data]);
  const shown = useMemo(
    () => (t ? sortRows(filterRows(t.rows, filter), sort) : []),
    [t, filter, sort],
  );

  const save = useMutation({
    mutationFn: (v: { row: AddressRow; cols: FlagCol[] }) =>
      opsRepo.setAddressFlags(v.row.city, v.row.address, v.cols),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['addressTable'] });
    },
    onError: (e: unknown) => {
      setErr(describeError(e));
      reportError('admin.setAddressFlags', e);
    },
  });

  const title = (
    <h2>
      <Hint text="تفکیکِ آدرس"
        help="هر ردیف یک ساختمان، با اجزایی که کرالر از آدرس درآورده. روی هر خانه بایست تا ببینی از کجا آمده (منبع / قاعده / مدل). قرمز یعنی شاید تکراری — همان شهر و خیابان و پلاک، همان پلاک در همان شهر، همان کدپستی، یا مختصاتِ کمتر از ۲۰۰ متر با ردیفِ دیگر. خانه‌ی خالی هرگز تکراری حساب نمی‌شود." />
    </h2>
  );

  if (q.isLoading) return <div className="card">{title}<p className="dim">…</p></div>;
  if (q.error) return <div className="card">{title}<p className="err">{describeError(q.error)}</p></div>;
  if (!t) return null;

  if (t.state !== 'ready') {
    return (
      <div className="card">
        {title}
        <p className="dim" style={{ marginTop: 10 }}>
          {t.state === 'missing' ? (
            <>این دیتابیس جدولِ <span className="mono ltr">crawler.admin_reports</span> را ندارد.</>
          ) : (
            <>
              گزارشِ <span className="mono ltr">address_table</span> هنوز به این دیتابیس نرسیده.
              کرالر آن را شبانه روی base می‌سازد و با بقیه‌ی گزارش‌های ادمین می‌فرستد.
            </>
          )}
        </p>
      </div>
    );
  }

  // سطرهای بیشتر از ۵ را `ui/collapseTables.ts` پشتِ «بیشتر» می‌برد.
  const rows = shown;
  const setCol = (k: ColKey, v: string) =>
    setFilter({ ...filter, cols: { ...filter.cols, [k]: v } });
  const toggleSort = (k: ColKey) => setSort(sort.col === k
    ? { col: k, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
    : { col: k, dir: COLUMNS.find((c) => c.key === k)?.numeric ? 'desc' : 'asc' });

  return (
    <div className="card">
      {title}

      <p className="dim" style={{ marginTop: 6 }}>
        {fmt(t.rows.length)} ساختمان
        {' · '}<span className={t.suspectRows > 0 ? 'err' : undefined}>{fmt(t.suspectRows)} با خانه‌ی قرمز</span>
        {' · '}{fmt(t.flaggedRows)} فلگ‌خورده
        {t.computedAt ? <> · ساخته‌شده {formatDateTime(t.computedAt)}</> : null}
      </p>
      {!t.flagsTable ? (
        <p className="err">
          این دیتابیس جدولِ <span className="mono ltr">crawler.address_flags</span> را ندارد؛
          فلگ زدن کار نمی‌کند تا مایگریشنِ ۰۰۵۵ِ کرالر روی آن اجرا شود.
        </p>
      ) : null}

      <div className="filters" style={{ marginTop: 10, marginBottom: 8 }}>
        <label className="markcell">
          <input type="checkbox" checked={filter.onlySuspect}
            onChange={(e) => { setFilter({ ...filter, onlySuspect: e.target.checked }); }} />
          فقط ردیف‌های قرمز
        </label>
        <button type="button" className="linkbtn" onClick={() => { setFilter(EMPTY_FILTER); }}>
          پاک کردنِ فیلترها
        </button>
        <span className="spacer" style={{ flex: 1 }} />
        <span className="dim">
          <Hint text={`${fmt(shown.length)} ردیف`}
            help="فیلترِ هر ستون متنی است و به حروف حساس نیست. «-» یعنی فقط خانه‌های خالی؛ روی ستون‌های عددی «>0»، «<3» یا «=0» هم کار می‌کند." />
        </span>
      </div>

      {err ? <p className="err" style={{ marginBottom: 8 }}>{err}</p> : null}

      <div className="scrollx">
        <table className="tbl addrtbl">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key}>
                  <button type="button" className="sortbtn" onClick={() => toggleSort(c.key)}
                    title={c.help}>
                    {c.label}
                    {sort.col === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
              ))}
            </tr>
            <tr className="colfilters">
              {COLUMNS.map((c) => (
                <th key={c.key}>
                  <input type="search" value={filter.cols[c.key] ?? ''} aria-label={`فیلترِ ${c.label}`} placeholder="فیلتر…"
                    onChange={(e) => setCol(c.key, e.target.value)} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={COLUMNS.length} className="dim">هیچ ردیفی با این فیلتر نیست.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id}>
                {COLUMNS.map((c) => {
                  const cls = [
                    r.suspects[c.key] ? 'sus' : '',
                    (r.flags as string[]).includes(c.key) ? 'flagged' : '',
                    c.numeric ? 'num' : '',
                    c.key === 'flags' ? 'flagcell' : '',
                  ].filter(Boolean).join(' ');
                  const tip = c.key === 'flags' ? cellProvenance(r, c.key) || undefined
                    : [cellProvenance(r, c.key), r.cells[c.key].length > CUT ? r.cells[c.key] : '']
                      .filter(Boolean).join('\n') || undefined;
                  return (
                    <td key={c.key} className={cls || undefined} title={tip}>
                      {c.key === 'flags' ? (
                        <FlagPicker row={r} busy={save.isPending} disabled={!t.flagsTable}
                          onSave={(cols) => save.mutate({ row: r, cols })} />
                      ) : <Cell row={r} col={c.key} />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
